import { format } from 'date-fns';

import { RegistryCompanyDto, RegistryJurisdiction } from '@/api/entity/types';
import { StepForms } from '@/stores/slices/entity-slice';

export const UK_COUNTRY_CODE = 'GB';
export const CH_COUNTRY_CODE = 'CH';

/**
 * Countries whose entities can be looked up in a public registry, mapped to
 * the BE `RegistryJurisdiction` the check endpoint expects. Note the codes
 * differ per source: entity forms store ISO country codes ('GB', 'CH') while
 * the BE jurisdiction enum uses 'UK' for Great Britain.
 */
const REGISTRY_JURISDICTION_BY_COUNTRY: Partial<Record<string, RegistryJurisdiction>> = {
  [UK_COUNTRY_CODE]: 'UK',
  [CH_COUNTRY_CODE]: 'CH',
};

export const getRegistryJurisdiction = (
  country: string | null | undefined,
): RegistryJurisdiction | null =>
  country ? (REGISTRY_JURISDICTION_BY_COUNTRY[country] ?? null) : null;

export type RegistryCheckStatus =
  | 'idle'
  | 'checking'
  | 'detected'
  | 'loaded'
  | 'notFound'
  | 'error';

export const isRegistryCheckPending = (
  country: string | null | undefined,
  status: RegistryCheckStatus,
): boolean =>
  getRegistryJurisdiction(country) !== null && (status === 'idle' || status === 'checking');

export const isPlausibleUkCompanyNumber = (value: string | null | undefined): boolean =>
  /^[A-Za-z]{0,2}[0-9]{6,8}$/.test(value?.trim() ?? '');

/**
 * Swiss UID as the BE Zefix adapter accepts it: 'CHE-123.456.789',
 * 'CHE123456789', or bare '123456789' (separators optional in any position —
 * the BE strips dots/dashes/whitespace and prefixes bare 9-digit UIDs).
 */
export const isPlausibleSwissUid = (value: string | null | undefined): boolean =>
  /^(CHE[-.\s]?)?\d{3}[.\s]?\d{3}[.\s]?\d{3}$/i.test(value?.trim() ?? '');

export const isPlausibleRegistryCompanyNumber = (
  country: string | null | undefined,
  value: string | null | undefined,
): boolean => {
  switch (getRegistryJurisdiction(country)) {
    case 'UK':
      return isPlausibleUkCompanyNumber(value);
    case 'CH':
      return isPlausibleSwissUid(value);
    default:
      return false;
  }
};

export const mapRegistryLegalFormToEntityType = (
  legalForm: string | null | undefined,
): string | null => {
  const value = legalForm?.trim().toLowerCase();
  if (!value) return null;

  const limitedCompanyForms = [
    // UK Companies House legal-form slugs
    'ltd',
    'plc',
    'private-limited-guarant-nsc',
    'private-limited-guarant-nsc-limited-exemption',
    'private-unlimited',
    'private-unlimited-nsc',
    'private-limited-shares-section-30-exemption',
    'old-public-company',
    'community-interest-company',
    // Zefix short names (CH): AG/SA (share company), GmbH/Sàrl (limited liability)
    'ag',
    'sa',
    'gmbh',
    'sàrl',
    'sarl',
  ];
  const partnershipForms = [
    // UK Companies House legal-form slugs
    'llp',
    'limited-partnership',
    'scottish-partnership',
    'general-partnership',
    // Zefix short names (CH): KlG (general partnership), KmG (limited partnership)
    'klg',
    'kmg',
  ];

  if (limitedCompanyForms.includes(value)) return 'LimitedCompany';
  if (partnershipForms.includes(value)) return 'Partnership';

  return null;
};

const joinAddressLines = (line1: string | null, line2: string | null): string | null => {
  const joined = [line1, line2]
    .map((line) => line?.trim())
    .filter(Boolean)
    .join(', ');
  return joined || null;
};

export interface RegistryFormMapping {
  updates: Partial<StepForms>;
  lockedFields: string[];
}

export const mapRegistryCompanyToEntityForm = (
  company: RegistryCompanyDto,
  /** ISO country code the check ran for — locked into the form ('GB' / 'CH'). */
  country: string,
): RegistryFormMapping => {
  const updates: Partial<StepForms> = {};
  const lockedFields: string[] = [];

  const lock = <K extends keyof StepForms>(field: K, value: StepForms[K]) => {
    updates[field] = value;
    lockedFields.push(field as string);
  };

  lock('country', country);

  if (company.name?.trim()) {
    lock('legalName', company.name.trim());
    updates.isLegalNameCorrect = true;
    updates.lastValidatedLegalName = company.name.trim();
  }

  if (company.number?.trim()) {
    lock('identificationNumber', company.number.trim().replace(/[^a-zA-Z0-9]/g, ''));
  }

  if (company.formationDate) {
    const date = new Date(company.formationDate);
    if (!Number.isNaN(date.getTime())) {
      lock('registeredAt', date);
    }
  }

  const mappedType = mapRegistryLegalFormToEntityType(company.legalForm);
  if (mappedType) {
    lock('type', mappedType);
  }

  const address = company.registeredAddress;
  if (address) {
    if (address.locality?.trim()) {
      lock('registeredAddressCity', address.locality.trim());
    }

    const line = joinAddressLines(address.line1, address.line2);
    if (line) {
      lock('registeredAddress', line);
    }

    if (address.postalCode?.trim()) {
      lock('registeredAddressPostalCode', address.postalCode.trim());
    }
  }

  return { updates, lockedFields };
};

const normalizeRegistryValue = (field: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';

  if (field === 'registeredAt') {
    const date = value instanceof Date ? value : new Date(value as string);
    return Number.isNaN(date.getTime()) ? '' : format(date, 'yyyy-MM-dd');
  }

  return String(value).trim().toLowerCase();
};

export const getMatchingRegistryLockedFields = (
  mapping: RegistryFormMapping,
  form: Partial<StepForms>,
): string[] =>
  mapping.lockedFields.filter((field) => {
    const registryValue = normalizeRegistryValue(field, mapping.updates[field as keyof StepForms]);
    if (!registryValue) return false;

    return registryValue === normalizeRegistryValue(field, form[field as keyof StepForms]);
  });
